import { Component, Injector, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Task, TaskAgendaReading, TasksService, TaskStatusEnum } from 'lib-trend-core';
import { catchError, merge, of, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { TaskRangeComponent } from '../task-range/task-range.component';
import { ActivityFilters } from '../task.component';

const REFRESH_MS = 60_000;

/** One cell of the month grid. */
export interface CalendarCell {
  day: string;
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  open: number;
  concluded: number;
  activities: Array<Task>;
}

/**
 * The calendar (SPEC-2026-10-crm-activities-cadence RF-43, plan AD-14): a Bootstrap month grid
 * with no calendar library — seven columns, one cell per day, counts of open and concluded
 * activities on it (open on the scheduled day, concluded on the conclusion day). Opening a day
 * lists its activities through the shared row. Reloads every 60 s and after every own action.
 */
@Component({
  selector: 'app-task-calendar',
  templateUrl: './task-calendar.component.html',
  styleUrls: ['./task-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TaskCalendarComponent extends AbstractComponent implements OnInit, OnDestroy {

  private currentFilters: ActivityFilters = {};
  private readonly reload$ = new Subject<void>();

  @Input() set filters(value: ActivityFilters) {
    this.currentFilters = value ?? {};
    this.reload$.next();
  }

  /** The first day of the month shown. */
  month = TaskCalendarComponent.firstOfMonth(new Date());
  cells: Array<CalendarCell> = [];
  selectedDay?: string;
  reading?: TaskAgendaReading;
  override loading = true;
  failed = false;

  /** Monday-first weekday headers, rendered through the date pipe so the locale names them. */
  readonly weekdays: Array<Date> = Array.from({ length: 7 }, (_, index) => new Date(2024, 0, 1 + index)); // 2024-01-01 is a Monday

  constructor(injector: Injector, private readonly tasksService: TasksService) {
    super(injector);
  }

  ngOnInit(): void {
    merge(timer(0, REFRESH_MS), this.reload$)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.tasksService.agenda({ ...this.currentFilters, from: this.fromKey, to: this.toKey }).pipe(catchError(() => of(null)))),
      )
      .subscribe((reading) => {
        this.loading = false;
        this.failed = !reading;
        if (reading) {
          this.reading = reading;
          this.cells = this.buildCells(reading.activities ?? []);
        }
      });
  }

  /** The grid starts on the Monday on or before the 1st and ends on the Sunday on or after the last day. */
  get gridStart(): Date {
    const start = new Date(this.month);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    return start;
  }

  get gridEnd(): Date {
    const end = new Date(this.month);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setDate(end.getDate() + ((7 - end.getDay()) % 7));
    return end;
  }

  get fromKey(): string {
    return TaskRangeComponent.dayKey(this.gridStart);
  }

  get toKey(): string {
    return TaskRangeComponent.dayKey(this.gridEnd);
  }

  get selectedCell(): CalendarCell | undefined {
    return this.cells.find((cell) => cell.day === this.selectedDay);
  }

  previous(): void {
    this.shiftMonth(-1);
  }

  next(): void {
    this.shiftMonth(1);
  }

  today(): void {
    this.month = TaskCalendarComponent.firstOfMonth(new Date());
    this.selectedDay = TaskRangeComponent.dayKey(new Date());
    this.loading = true;
    this.reload$.next();
  }

  select(cell: CalendarCell): void {
    this.selectedDay = this.selectedDay === cell.day ? undefined : cell.day;
  }

  onChanged(): void {
    this.reload$.next();
  }

  private shiftMonth(direction: 1 | -1): void {
    const month = new Date(this.month);
    month.setMonth(month.getMonth() + direction);
    this.month = TaskCalendarComponent.firstOfMonth(month);
    this.selectedDay = undefined;
    this.loading = true;
    this.reload$.next();
  }

  private buildCells(activities: Array<Task>): Array<CalendarCell> {
    const byDay = new Map<string, Array<Task>>();
    for (const activity of activities) {
      if (!activity.day) continue;
      if (!byDay.has(activity.day)) byDay.set(activity.day, []);
      byDay.get(activity.day).push(activity);
    }

    const todayKey = TaskRangeComponent.dayKey(new Date());
    const cells: Array<CalendarCell> = [];
    for (let date = new Date(this.gridStart); date.getTime() <= this.gridEnd.getTime(); date.setDate(date.getDate() + 1)) {
      const day = TaskRangeComponent.dayKey(date);
      const list = byDay.get(day) ?? [];
      cells.push({
        day,
        date: new Date(date),
        inMonth: date.getMonth() === this.month.getMonth(),
        isToday: day === todayKey,
        open: list.filter((activity) => activity.status !== TaskStatusEnum.DONE).length,
        concluded: list.filter((activity) => activity.status === TaskStatusEnum.DONE).length,
        activities: list,
      });
    }
    return cells;
  }

  static firstOfMonth(date: Date): Date {
    const first = new Date(date);
    first.setDate(1);
    first.setHours(0, 0, 0, 0);
    return first;
  }

  override ngOnDestroy(): void {
    this.reload$.complete();
    super.ngOnDestroy();
  }
}
